import { useState, type FormEvent } from "react";
import { ArrowRight, Check, AlertCircle, Loader2, Briefcase, User, Shield, Wallet } from "lucide-react";

const API_URL = "https://api.hirenfire.me/api/waitlist/join";

export default function CTA() {
  const [telegram, setTelegram] = useState("");
  const [role, setRole] = useState<"worker" | "agency" | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!telegram.trim() || !role || loading) return;

    setLoading(true);
    setError("");

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telegram: telegram.trim(), role, source: "landing_cta" }),
      });

      if (res.status === 429) {
        setError("Too many attempts. Please try again later.");
        return;
      }

      const data = await res.json();
      if (!res.ok) {
        setError(data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      setSubmitted(true);
    } catch {
      setError("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="cta" className="section-padding-loose relative overflow-hidden">
      {/* Background — warm accent glow */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-bg-primary via-bg-secondary to-bg-primary" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 30% 50%, rgba(245,158,11,0.08) 0%, transparent 60%)",
          }}
        />
      </div>

      <div className="section-container relative">
        <div className="cta-content flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
          {/* Left: headline + subtitle */}
          <div className="lg:w-1/2">
            <div className="accent-line-amber mb-8">
              <span className="text-accent-amber uppercase tracking-widest text-xs font-mono mb-3 block">
                Join the Waitlist
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                Ready to change{" "}
                <span className="text-gradient-warm">how you hire?</span>
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed">
                Be first to access the privacy-first hiring platform built for the modern workforce.
              </p>
            </div>

            {/* Trust badges */}
            <div className="flex flex-col gap-3 text-sm text-gray-500">
              <span className="flex items-center gap-2">
                <Check size={14} className="text-green-500" />
                Free to join — no credit card required
              </span>
              <span className="flex items-center gap-2">
                <Check size={14} className="text-green-500" />
                Early access when we launch
              </span>
              <span className="flex items-center gap-2">
                <Check size={14} className="text-green-500" />
                Cancel anytime
              </span>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:w-1/2 w-full">
            {!submitted ? (
              <div className="space-y-6">
                {/* Role selector cards */}
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => { setRole("worker"); setError(""); }}
                    className={`group relative p-5 rounded-none rounded-br-2xl rounded-tr-2xl border-l-[3px] transition-all duration-300 text-left ${
                      role === "worker"
                        ? "border-l-accent-purple bg-accent-purple/10"
                        : "border-l-surface-border bg-surface/30 hover:border-l-gray-500 hover:bg-surface/50"
                    }`}
                  >
                    {role === "worker" && (
                      <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-accent-purple flex items-center justify-center">
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors ${
                      role === "worker" ? "bg-accent-purple/20 text-accent-purple" : "bg-surface text-gray-500"
                    }`}>
                      <User size={20} />
                    </div>
                    <h3 className={`font-display text-base font-bold mb-1 ${role === "worker" ? "text-white" : "text-gray-300"}`}>
                      I'm a Worker
                    </h3>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      VA, Chatter, or Freelancer looking for verified gigs
                    </p>
                    <div className="flex items-center gap-3 mt-3 pt-2 border-t border-surface-border/30">
                      <span className="flex items-center gap-1 text-[10px] text-gray-600">
                        <Wallet size={10} /> Crypto pay
                      </span>
                      <span className="flex items-center gap-1 text-[10px] text-gray-600">
                        <Shield size={10} /> Anonymous
                      </span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => { setRole("agency"); setError(""); }}
                    className={`group relative p-5 rounded-none rounded-br-2xl rounded-tr-2xl border-l-[3px] transition-all duration-300 text-left ${
                      role === "agency"
                        ? "border-l-accent-blue bg-accent-blue/10"
                        : "border-l-surface-border bg-surface/30 hover:border-l-gray-500 hover:bg-surface/50"
                    }`}
                  >
                    {role === "agency" && (
                      <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-accent-blue flex items-center justify-center">
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors ${
                      role === "agency" ? "bg-accent-blue/20 text-accent-blue" : "bg-surface text-gray-500"
                    }`}>
                      <Briefcase size={20} />
                    </div>
                    <h3 className={`font-display text-base font-bold mb-1 ${role === "agency" ? "text-white" : "text-gray-300"}`}>
                      I'm Hiring
                    </h3>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Agency or Manager looking for vetted workers
                    </p>
                    <div className="flex items-center gap-3 mt-3 pt-2 border-t border-surface-border/30">
                      <span className="flex items-center gap-1 text-[10px] text-gray-600">
                        <Shield size={10} /> Escrow
                      </span>
                      <span className="flex items-center gap-1 text-[10px] text-gray-600">
                        <User size={10} /> Verified
                      </span>
                    </div>
                  </button>
                </div>

                {/* Telegram handle — stacked (not inline) */}
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-mono">@</span>
                    <input
                      type="text"
                      value={telegram}
                      onChange={(e) => { setTelegram(e.target.value.replace(/^@/, '')); setError(""); }}
                      placeholder="your_telegram"
                      required
                      disabled={loading}
                      className="w-full pl-8 pr-4 py-4 rounded-none rounded-br-xl rounded-tr-xl bg-surface/50 border border-surface-border text-white placeholder:text-gray-500 focus:outline-none focus:border-accent-amber/50 focus:ring-2 focus:ring-accent-amber/20 transition-all disabled:opacity-50 font-mono"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !role}
                    className="w-full py-4 rounded-none rounded-br-xl rounded-tr-xl bg-gradient-warm text-white font-display font-bold text-base hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <><Loader2 size={18} className="animate-spin" /> Joining...</>
                    ) : (
                      <><ArrowRight size={18} /> Join Waitlist</>
                    )}
                  </button>
                </form>

                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle size={14} />
                    {error}
                  </div>
                )}

                {!role && (
                  <p className="text-sm text-gray-600">Select your role above to join</p>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center lg:items-start gap-4 py-8">
                <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check size={28} className="text-green-400" />
                </div>
                <div className="text-center lg:text-left">
                  <p className="font-display text-xl text-green-400 font-semibold mb-1">You're on the list!</p>
                  <p className="text-gray-400">We'll notify you on Telegram when we launch.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
